import { useState, useContext } from "react";
import dayjs from "dayjs";
import { useSWRConfig } from "swr";

import { Grid, Typography, Avatar } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faMoneyCheck,
  faExclamation,
  faSyncAlt
} from "@fortawesome/free-solid-svg-icons";

import { LoadingButton } from "@aclymatepackages/atoms";
import { ucFirstLetters, formatDollars } from "@aclymatepackages/formatters";

import GrayPaper from "../../atoms/GrayPaper";

import { useAccountData, useAuth } from "../../../helpers/firebase";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../../helpers/analytics";

import usePurchasingContext from "../../../helpers/contexts/purchasing";
import usePortfoliosContext from "../../../helpers/contexts/offsetsPortfolios";
import { StripeCustomerContext } from "../../../helpers/contexts/stripeCustomer";

const FREQUENCY_DISPLAY_MAP = {
  monthly: "month",
  quarterly: "quarter",
  yearly: "year"
};

const FREQUENCY_MONTHS_MAP = {
  monthly: 1,
  quarterly: 3,
  yearly: 12
};

const SubscriptionConfirmation = ({
  setSubscriptionCreated,
  setSubscriptionId
}) => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { id: portfolioId, name: portfolioName } = selectedPortfolio || {};

  const { budgetDollars, frequency, firstChargeDate } = usePurchasingContext();

  const { paymentMethod } = useContext(StripeCustomerContext);
  const { paymentMethodId, card, us_bank_account } = paymentMethod || {};
  const { last4: cardLast4, brand } = card || {};
  const { bank_name, last4: bankLast4 } = us_bank_account || {};

  const [{ stripeCustomerId, billing }] = useAccountData();
  const [{ email }] = useAuth();
  const { mutate } = useSWRConfig();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const frequencyLabel = FREQUENCY_DISPLAY_MAP[frequency];
  const firstChargeDateDayjs = firstChargeDate
    ? dayjs(firstChargeDate)
    : dayjs().add(FREQUENCY_MONTHS_MAP[frequency] || 1, "month");
  const firstChargeDateDisplay = firstChargeDateDayjs.format("MMMM D, YYYY");

  const startSubscription = async () => {
    setSubmitLoading(true);
    setSubmitError(null);

    const accountId = window.sessionStorage.getItem("accountId");
    const { stripeCustomerId: billingStripeCustomerId } = billing || {};

    const callback = (data) => {
      if (data?.error) {
        setSubmitLoading(false);
        return setSubmitError(data.message || "Unable to start subscription.");
      }
      analyticsTrack("CNaught Subscription Created", {
        portfolioId,
        portfolioName,
        budgetDollars,
        frequency
      });
      mutate("offset-subscriptions");
      setSubscriptionId?.(data?.id);
      setSubscriptionCreated?.(true);
      return setSubmitLoading(false);
    };

    return await fetchOurApi({
      path: "/offset-subscriptions/create",
      method: "POST",
      data: {
        accountId,
        userEmail: email,
        budgetDollars,
        frequency,
        portfolioId,
        paymentMethodId,
        paymentType: us_bank_account ? "ach" : "card",
        stripeCustomerId: stripeCustomerId || billingStripeCustomerId,
        ...(firstChargeDate
          ? { firstChargeDate: dayjs(firstChargeDate).toISOString() }
          : {})
      },
      callback
    });
  };

  const paymentDisclaimer = us_bank_account
    ? {
        icon: faMoneyCheck,
        text: `Each cycle we will bill your ${ucFirstLetters(
          bank_name
        )} bank account ending in ${bankLast4}. ACH bank payments take a few business days to settle.`
      }
    : {
        icon: faCreditCard,
        text: `Each cycle we will bill your ${ucFirstLetters(
          brand
        )} credit card ending in ${cardLast4}.`
      };

  const disclaimers = [
    paymentDisclaimer,
    {
      icon: faSyncAlt,
      text: `Your next charge of approximately ${formatDollars(
        budgetDollars
      )} is scheduled for ${firstChargeDateDisplay}. You can pause or cancel at any time.`
    },
    {
      icon: faExclamation,
      text: "Each cycle's offsets are non-refundable since they are retired immediately at purchase."
    }
  ];

  return (
    <GrayPaper p={2}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="h3">
            Confirm your offset subscription
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            We will buy offsets from {portfolioName} every {frequencyLabel} up
            to your budget of {formatDollars(budgetDollars)}.
          </Typography>
        </Grid>
        {disclaimers.map(({ icon, text }, idx) => (
          <Grid
            key={`subscription-disclaimer-${idx}`}
            item
            container
            spacing={2}
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item>
              <Avatar>
                <FontAwesomeIcon icon={icon} />
              </Avatar>
            </Grid>
            <Grid item>
              <Typography variant="body1">{text}</Typography>
            </Grid>
          </Grid>
        ))}
        {submitError && (
          <Grid item>
            <Typography variant="body2" color="error" align="center">
              {submitError}
            </Typography>
          </Grid>
        )}
        <Grid item container justifyContent="center">
          <Grid item>
            <LoadingButton
              disabled={!budgetDollars || !paymentMethodId}
              label="start subscription"
              variant="contained"
              color="secondary"
              onClick={startSubscription}
              isLoading={submitLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    </GrayPaper>
  );
};

export default SubscriptionConfirmation;
